import { Desktop, Globe } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { describeImportSource, readImportSource } from '@/features/backup/import-source';
import { cn } from '@/lib/cn';
import { Tooltip } from '@/components/primitives/tooltip';
import { describePlatform } from './index';

/**
 * 工具列的版本身分標示（desktop-app.md 4.2）。
 *
 * 兩個版本的資料在瀏覽器安全模型下必然分家，同時開著不會壞，但永遠不會同步。
 * 使用者要能一眼看出「現在打的字會存到哪一邊」，否則換一邊打開就會以為資料不見了。
 */
export function PlatformBadge({ className }: { className?: string }) {
  const identity = useMemo(() => describePlatform(), []);
  // 匯入完成後會重新載入或至少換頁，讀一次就夠，不必訂閱。
  const imported = useMemo(() => describeImportSource(readImportSource()), []);
  const Icon = identity.kind === 'desktop' ? Desktop : Globe;

  return (
    <Tooltip label={imported ? `${identity.detail}（${imported}）` : identity.detail}>
      <output
        aria-label={`目前是${identity.label}，資料位置 ${identity.location}`}
        className={cn(
          'inline-flex h-9 max-w-[16rem] shrink-0 items-center gap-2 rounded-full border border-border whitespace-nowrap',
          'bg-surface px-3 text-[0.8125rem] font-medium text-text-muted',
          className,
        )}
      >
        <Icon size={15} />
        <span className="shrink-0">{identity.label}</span>
        {/* 窄畫面時只留身分，位置退到 tooltip，免得把工具列擠爆。 */}
        <span aria-hidden="true" className="hidden shrink-0 text-border-strong 2xl:inline">
          ・
        </span>
        <span className="hidden min-w-0 truncate font-normal 2xl:inline">
          {imported ?? identity.location}
        </span>
      </output>
    </Tooltip>
  );
}
